import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCampaignSuccessMetricsAnalytics, CampaignAnalyticsResponse } from '../../services/api';
import LoadingState from '../Common/LoadingState';
import AnalyticsMetricCards from './AnalyticsMetricCards';
import MetricsGrid from './MetricsGrid';
import { HiExclamationCircle, HiRefresh, HiFilter } from 'react-icons/hi';

interface CampaignAnalyticsProps {
  campaignId: string;
}

const CampaignAnalytics: React.FC<CampaignAnalyticsProps> = ({ campaignId }) => {
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  // Fetch analytics data
  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ['campaignAnalytics', campaignId],
    queryFn: () => getCampaignSuccessMetricsAnalytics(campaignId),
    enabled: !!campaignId,
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
  });

  if (isLoading) {
    return <LoadingState message="Loading analytics..." />;
  }

  if (error || !data) {
    return (
      <div className="bg-[var(--card)] rounded-lg p-8 shadow-sm border border-[var(--border)]">
        <div className="text-center">
          <HiExclamationCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[var(--foreground)] mb-2">
            Failed to Load Analytics
          </h3>
          <p className="text-[var(--foreground-muted)] mb-4">
            {error instanceof Error ? error.message : 'An error occurred while loading analytics.'}
          </p>
          <button
            onClick={() => refetch()}
            className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white px-4 py-2 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Check if there are no metrics
  if (!data.success_metrics || data.success_metrics.length === 0) {
    return (
      <div className="bg-[var(--card)] rounded-lg p-8 shadow-sm border border-[var(--border)]">
        <div className="text-center">
          <div className="mb-4">
            <svg className="h-16 w-16 text-gray-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-[var(--foreground)] mb-2">
            No Analytics Available
          </h3>
          <p className="text-[var(--foreground-muted)]">
            Analytics will appear here once calls are completed and analyzed.
          </p>
        </div>
      </div>
    );
  }

  // Get unique categories and priorities for filtering
  const availableCategories = Array.from(new Set(data.success_metrics.map(m => m.category)));
  const availablePriorities = Array.from(new Set(data.success_metrics.map(m => m.priority)));

  return (
    <div className="space-y-6">
      {/* Header with refresh button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[var(--foreground)]">Campaign Analytics</h2>
          <p className="text-sm text-[var(--foreground-muted)] mt-1">
            Last updated: {new Date(data.analysis_metadata.generated_at).toLocaleString()}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isRefetching}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--border)] 
            ${isRefetching 
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
              : 'bg-[var(--card)] text-[var(--foreground)] hover:bg-gray-50'
            } transition-colors`}
        >
          <HiRefresh className={`h-5 w-5 ${isRefetching ? 'animate-spin' : ''}`} />
          {isRefetching ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Top-level Metric Cards */}
      <AnalyticsMetricCards 
        callSummary={data.call_summary} 
        insights={data.insights} 
      />

      {/* Filter Section (optional - can be implemented later) */}
      {availableCategories.length > 1 && (
        <div className="bg-[var(--card)] rounded-lg p-4 shadow-sm border border-[var(--border)]">
          <div className="flex items-center gap-2 mb-3">
            <HiFilter className="h-5 w-5 text-[var(--foreground-muted)]" />
            <h3 className="text-sm font-medium text-[var(--foreground)]">Filter Metrics</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {availablePriorities.map(priority => (
              <button
                key={priority}
                onClick={() => {
                  setSelectedPriorities(prev => 
                    prev.includes(priority) 
                      ? prev.filter(p => p !== priority)
                      : [...prev, priority]
                  );
                }}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  selectedPriorities.includes(priority)
                    ? 'bg-[var(--primary)] text-white border-[var(--primary)]'
                    : 'bg-white text-[var(--foreground)] border-[var(--border)] hover:bg-gray-50'
                }`}
              >
                {priority.charAt(0).toUpperCase() + priority.slice(1)}
              </button>
            ))}
            <div className="w-px h-6 bg-[var(--border)] mx-2" />
            {availableCategories.slice(0, 5).map(category => (
              <button
                key={category}
                onClick={() => {
                  setSelectedCategories(prev => 
                    prev.includes(category) 
                      ? prev.filter(c => c !== category)
                      : [...prev, category]
                  );
                }}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  selectedCategories.includes(category)
                    ? 'bg-[var(--primary)] text-white border-[var(--primary)]'
                    : 'bg-white text-[var(--foreground)] border-[var(--border)] hover:bg-gray-50'
                }`}
              >
                {category}
              </button>
            ))}
            {(selectedPriorities.length > 0 || selectedCategories.length > 0) && (
              <>
                <div className="w-px h-6 bg-[var(--border)] mx-2" />
                <button
                  onClick={() => {
                    setSelectedPriorities([]);
                    setSelectedCategories([]);
                  }}
                  className="px-3 py-1 text-sm text-red-600 hover:text-red-700"
                >
                  Clear filters
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Metrics Grid */}
      <MetricsGrid 
        metrics={data.success_metrics}
        filters={{
          priority: selectedPriorities.length > 0 ? selectedPriorities : undefined,
          category: selectedCategories.length > 0 ? selectedCategories : undefined,
        }}
      />

      {/* Data Quality Issues (if any) */}
      {data.insights.data_quality_summary.quality_issues && 
       data.insights.data_quality_summary.quality_issues.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-yellow-900 mb-2">
            Data Quality Issues
          </h4>
          <ul className="list-disc list-inside text-sm text-yellow-800">
            {data.insights.data_quality_summary.quality_issues.map((issue, idx) => (
              <li key={idx}>{issue}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CampaignAnalytics;
